'use es6';

import { forwardRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { getEmptyImage } from 'react-dnd-compat';
import MenuItem from 'simple-menus-lib/components/menu/MenuItem';
import MenuNodeDivider from 'simple-menus-lib/components/menu/MenuNodeDivider';
import menuDragSource from 'simple-menus-lib/components/dnd/decorators/menuDragSource';
import { setIsDragging } from 'simple-menus-core/actions/Application';
import DropPositions from 'simple-menus-core/enums/DropPositions';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TopDivider = ({
  id,
  depth,
  index,
  parentId,
  menuId,
  isDragging,
  isParentDragging,
  readOnly
}) => {
  if (index === 0 && depth === 0) {
    return /*#__PURE__*/_jsx(MenuNodeDivider, {
      id: id,
      depth: depth,
      position: DropPositions.TOP,
      parentId: parentId,
      menuId: menuId,
      isParentDragging: isDragging || isParentDragging,
      readOnly: readOnly
    });
  }
  return null;
};
const MenuNode = /*#__PURE__*/forwardRef((props, ref) => {
  const [collapsed, setIsCollapsed] = useState(false);
  const {
    children = [],
    className,
    depth,
    depthToNextSibling,
    hierarchy,
    index,
    id,
    menuId,
    parentId,
    hasSibling,
    isDragging,
    isLastChild,
    isParentDragging,
    readOnly,
    connectDragSource,
    connectDragPreview,
    getMenuComponent,
    moveNode
  } = props;
  const nodeClassNames = classNames(className, 'simple-menus-menu-node', isDragging && 'is--dragged');
  useEffect(() => {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  }, [connectDragPreview]);
  const toggleCollapse = () => {
    setIsCollapsed(isCollapsed => !isCollapsed);
  };
  const MenuComponent = getMenuComponent(true);
  return connectDragSource( /*#__PURE__*/_jsx("div", {
    children: /*#__PURE__*/_jsxs("div", {
      className: nodeClassNames,
      ref: ref,
      children: [/*#__PURE__*/_jsx(TopDivider, {
        id: id,
        depth: depth,
        index: index,
        parentId: parentId,
        menuId: menuId,
        isDragging: isDragging,
        isParentDragging: isParentDragging,
        readOnly: readOnly
      }), /*#__PURE__*/_jsx(MenuItem, {
        id: id,
        parentId: parentId,
        moveNode: moveNode,
        isParentDragging: isDragging || isParentDragging,
        depth: depth,
        hasChildren: Boolean(children.length),
        toggleCollapse: toggleCollapse,
        isCollapsed: collapsed,
        menuId: menuId,
        readOnly: readOnly
      }), /*#__PURE__*/_jsx(MenuNodeDivider, {
        id: id,
        depth: depth,
        parentId: collapsed ? null : parentId,
        menuId: menuId,
        isCollapsed: collapsed,
        isParentDragging: isDragging || isParentDragging,
        isLastChild: isLastChild,
        hasChildren: !!children.length,
        hasSibling: hasSibling,
        depthToNextSibling: depthToNextSibling,
        hierarchy: hierarchy,
        readOnly: readOnly
      }), /*#__PURE__*/_jsx(MenuComponent, {
        parentId: id,
        nodes: children,
        getMenuComponent: getMenuComponent,
        moveNode: moveNode,
        isParentDragging: isDragging || isParentDragging,
        depth: depth + 1,
        isCollapsed: collapsed,
        menuId: menuId,
        depthToNextSibling: depthToNextSibling,
        hierarchy: [...hierarchy, id],
        readOnly: readOnly
      })]
    })
  }));
});
MenuNode.propTypes = {
  children: PropTypes.arrayOf(PropTypes.any).isRequired,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  depthToNextSibling: PropTypes.number,
  hierarchy: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  index: PropTypes.number.isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  menuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hasSibling: PropTypes.bool,
  isDragging: PropTypes.bool,
  isLastChild: PropTypes.bool,
  isParentDragging: PropTypes.bool,
  readOnly: PropTypes.bool,
  connectDragSource: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  getMenuComponent: PropTypes.func.isRequired,
  moveNode: PropTypes.func.isRequired
};
const mapDispatchToProps = {
  setIsDragging
};
export default compose(connect(null, mapDispatchToProps), menuDragSource())(MenuNode);